/**
 * Permission-Protected Route Component
 * Restricts access to routes based on user permissions
 * 
 * Usage:
 * <Route 
 *   path="billing" 
 *   element={
 *     <PermissionProtectedRoute requiredPermission="view_billing">
 *       <Billing />
 *     </PermissionProtectedRoute>
 *   } 
 * />
 */

import { Navigate } from 'react-router-dom';
import { useIntegratedSidebar } from '../hooks/useIntegratedSidebar';
import { useAuth } from '../AuthContext';

export function PermissionProtectedRoute({
  children,
  requiredPermission,
  requiredPermissions,
  requiredAnyPermissions,
  requiredAllPermissions,
  requiredSection,
  fallbackRole,
  fallbackPath = '/admin'
}) {
  const { hasPermission, canSeeSection, loading, error, permissions } = useIntegratedSidebar();
  const { user } = useAuth();
  
  // Show loading state while permissions are being fetched
  if (loading) {
    return (
      <div role="status" className="flex items-center justify-center h-screen">
        <div aria-hidden="true" className="h-10 w-10 animate-spin rounded-full border-4 border-gray-200 border-t-teal-600 dark:border-gray-700 dark:border-t-teal-500" />
        <span className="sr-only">Verifying permissions</span>
      </div>
    );
  }
  
  // super_admin has universal access on the server (authMiddleware returns
  // early for it), so denying here contradicts the API and locks the account
  // out of pages it can freely call.
  if (user?.role === 'super_admin') return children;

  // Fail-closed on an EMPTY permission set is wrong. When /admin/my-sidebar
  // fails or has not populated, permissions is {} with loading already false,
  // so every guarded route silently redirected — an account holding every
  // permission was bounced off Time Tracking AND Appointments at once, with
  // nothing on screen to explain it. Treat "we could not load permissions" as
  // a recoverable error, not as a denial.
  const permissionsUnavailable = !!error || Object.keys(permissions || {}).length === 0;
  if (permissionsUnavailable) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center max-w-sm px-6">
          <p className="text-gray-900 dark:text-white font-medium">Could not load your permissions</p>
          <p className="text-gray-600 dark:text-gray-400 text-sm mt-2">
            This is usually temporary. Reload the page to try again.
          </p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 cursor-pointer rounded-lg bg-teal-600 px-4 py-2 text-sm font-medium text-white transition-colors duration-200 hover:bg-teal-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }

  // Check permission (or fallback role)
  const hasRoleFallback = fallbackRole
    ? (Array.isArray(fallbackRole) ? fallbackRole.includes(user?.role) : user?.role === fallbackRole)
    : false;
  const anyPermissions = requiredAnyPermissions || requiredPermissions;
  const hasAnyPermissions = Array.isArray(anyPermissions) && anyPermissions.length > 0
    ? anyPermissions.some(permission => hasPermission(permission))
    : null;
  const hasAllPermissions = Array.isArray(requiredAllPermissions) && requiredAllPermissions.length > 0
    ? requiredAllPermissions.every(permission => hasPermission(permission))
    : null;
  const hasAccess = hasRoleFallback || (
    requiredPermission
      ? hasPermission(requiredPermission)
      : hasAllPermissions !== null
      ? hasAllPermissions
      : hasAnyPermissions !== null
      ? hasAnyPermissions
      : requiredSection
      ? canSeeSection(requiredSection)
      : true
  );
  
  if (!hasAccess) {
    const requirementLabel = requiredPermission
      || (Array.isArray(requiredAllPermissions) ? requiredAllPermissions.join(',') : null)
      || (Array.isArray(anyPermissions) ? anyPermissions.join(',') : null)
      || requiredSection;
    console.warn(`🚫 Access denied - Missing permission: ${requirementLabel}`);
    return <Navigate to={fallbackPath} replace />;
  }
  
  return children;
}

export default PermissionProtectedRoute;
